import { FormControl, FormControlProps, FormLabel } from '@chakra-ui/react'
import { ReactNode } from 'react'

/**
 * Altura única de todo controle de filtro do painel.
 *
 * Mora aqui, e não no `theme.ts`: um `defaultProps` de `size` em `Input`/`Select` pegaria
 * também os campos de formulário dos modais, que são `md` de propósito. Densidade é da
 * barra de filtros, não do input.
 */
export const FILTER_CONTROL_SIZE = 'sm'

/** Cada campo cresce por igual e quebra a linha a partir de ~200px. */
export const FILTER_FIELD_FLEX = '1 1 200px'

/**
 * Teto de crescimento de um campo. Sem ele, uma barra com dois campos dá 900px a um `<Select>`
 * de três opções — o campo cresce até sobrar espaço, e sobra muito. Com o teto, a barra para de
 * crescer e o resto da linha fica vazio, que é como uma barra de filtros deve parecer.
 */
export const FILTER_FIELD_MAX_W = '320px'

/** Props de layout que os campos de filtro repassam ao `FormControl`, e não ao controle interno. */
export interface IFilterLayoutProps {
  /** Sobrescreve o `flex` padrão — use para um campo que precisa de mais (ou menos) espaço que os vizinhos. */
  flex?: FormControlProps['flex']
  maxW?: FormControlProps['maxW']
  minW?: FormControlProps['minW']
}

export interface IFilterFieldProps extends Omit<FormControlProps, 'label'>, IFilterLayoutProps {
  /** Rótulo do campo, em pt-BR. Sem dois-pontos no fim. */
  label: string
  /** Só quando o controle interno não é associado sozinho pelo `FormControl` (ex.: dois inputs). */
  htmlFor?: string
  children: ReactNode
}

/**
 * Campo da barra de filtros: rótulo + controle, com o único estilo de `FormLabel` das listagens.
 *
 * Use os campos prontos (`FilterSearch`, `FilterSelect`, `FilterDateRange`) sempre que servirem.
 * Este é o escape hatch para um controle que ainda não tem componente próprio — um `type="month"`,
 * um `SearchableSelect` — e que só precisa herdar rótulo e geometria.
 */
export default function FilterField({
  label,
  htmlFor,
  children,
  flex = FILTER_FIELD_FLEX,
  maxW = FILTER_FIELD_MAX_W,
  ...rest
}: IFilterFieldProps) {
  return (
    // `minW="0"`: sem isso um item de flex não encolhe abaixo da largura do próprio conteúdo
    // e um select com opção longa empurra os vizinhos para fora da barra.
    <FormControl flex={flex} maxW={maxW} minW="0" {...rest}>
      {/* Semibold e `gray.700`: o rótulo precisa se separar do texto do próprio controle, que é
          cinza claro enquanto o campo está vazio ("Todos", o placeholder da busca). Em peso normal
          os dois pesavam igual e a barra virava um bloco cinza só. */}
      <FormLabel htmlFor={htmlFor} fontSize="sm" fontWeight="semibold" color="gray.700" mb="1">
        {label}
      </FormLabel>
      {children}
    </FormControl>
  )
}
